import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    if (value.length < 6) return 'Password must be at least 6 characters long';
    return '';
  };

  const handleBlur = (field: 'email' | 'password') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Only validate on submit if fields haven't been touched
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setTouched(prev => ({ ...prev, password: true }));
      return;
    }

    try {
      logger.info('Attempting to sign up user', { email });
      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) {
        logger.error('Sign up error', signUpError);
        
        if (signUpError.message.includes('unique')) {
          setError('This email is already registered. Please sign in instead.');
        } else if (signUpError.message.includes('valid email')) {
          setError('Please enter a valid email address.');
        } else {
          setError(signUpError.message);
        }
        return;
      }

      if (!user) {
        setError('Failed to create account. Please try again.');
        return;
      }

      logger.info('Creating new user profile', { userId: user.id });
      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert([{ user_id: user.id }]);

      if (profileError) {
        logger.error('Profile creation error', profileError);
        setError('Account created but profile setup failed. Please contact support.');
        return;
      }
      navigate('/pricing');
    } catch (err) {
      logger.error('Unexpected error during sign up', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Show password validation error only after field is touched
  const passwordError = touched.password ? validatePassword(password) : '';

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-2xl mb-2">
          Become The #1 Must-Hire Candidate
        </h1>
        <h2 className="text-2xl text-gray-400">
          Create your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded animate-fadeIn">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 ${
                  passwordError ? 'border-red-300' : 'border-gray-300'
                }`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur('password')}
              />
              {passwordError ? (
                <p className="mt-1 text-sm text-red-600">{passwordError}</p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  Must be at least 6 characters long
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign up
            </button>

            <div className="text-sm text-center">
              Already have an account?{' '}
              <Link to="/signin" className="text-blue-600 hover:text-blue-500">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}